/**
 * MentionMenu — autocomplete dropdown for `@mention` specialist
 * routing in Ask Argus chat (feat/multi-agent-in-chat, Pattern C).
 *
 * Companion to the slash-command popup in `chat-input.tsx`. Renders
 * just above the composer when the user is mid-mention, filtered to
 * agents that:
 *
 *   1. Are attached to this conversation (per
 *      `conversation.attached_tools`, fetched by the parent).
 *   2. Have `kind === 'specialist'` or `'ask_argus'` (the routable
 *      kinds — helper agents don't appear in the autocomplete).
 *   3. Match the partial slug the user has typed after `@`.
 *
 * Keyboard navigation:
 *   - ↑ / ↓     move selection
 *   - Enter     accept the highlighted candidate
 *   - Tab       same as Enter
 *   - Escape    close without inserting
 *
 * Mouse / pointer:
 *   - Click any item to accept.
 *
 * Visual conventions:
 *   - Per-agent avatar (ArgusEye for Ask Argus, code/terminal icon
 *     for Argus Code, generic Bot icon for other specialists).
 *   - Display name + slug (slug rendered subdued — it's what gets
 *     inserted in the textarea, not what the user reads).
 *
 * Reuses production components — Button, cn utility, ArgusEye icon —
 * does not create custom popovers (founder rule: feedback_use_production_components.md).
 */

import { useEffect, useRef } from 'react';
import { Bot, Code2 } from 'lucide-react';
import { ArgusEye } from '@/components/icons';
import { cn } from '@/lib/utils';

/**
 * Shape of one autocomplete candidate. Mirrors the per-agent slice
 * returned by GET /custom-agents and the system-agent rows we ship in
 * agents.json.
 */
export interface MentionCandidate {
  /** Slug as displayed in the composer (`argus-code`, not `system.argus_code`). */
  mentionSlug: string;
  /** Full DB slug. Used for hover / debug only. */
  dbSlug: string;
  /** User-facing display name. */
  displayName: string;
  /** Short description for the second line. */
  description: string;
  /** Agent kind — drives the avatar choice. */
  kind: 'ask_argus' | 'specialist' | string;
}

interface MentionMenuProps {
  /** True when the dropdown should be visible. */
  open: boolean;
  /** Filtered + sorted list to render. Empty list = show "no matches" hint. */
  candidates: MentionCandidate[];
  /** Index of the currently highlighted candidate. */
  highlightedIndex: number;
  /** Called when the user picks a candidate (keyboard or click). */
  onSelect: (candidate: MentionCandidate) => void;
  /** Called when the highlight changes (mouse hover). */
  onHighlight: (index: number) => void;
}

/**
 * Map an agent kind to its avatar component. Founder spec:
 *
 *   - Ask Argus  → ArgusEye (the brand mark)
 *   - Argus Code → Code2 (terminal/code icon)
 *   - other specialists → Bot (generic)
 */
function AgentAvatar({ kind, mentionSlug }: { kind: string; mentionSlug: string }) {
  if (kind === 'ask_argus' || mentionSlug === 'system.ask_argus' || mentionSlug === 'argus' || mentionSlug === 'ask-argus') {
    return <ArgusEye className="h-5 w-5" />;
  }
  if (mentionSlug === 'argus-code' || mentionSlug === 'argus_code') {
    return <Code2 className="h-5 w-5 text-purple-500" />;
  }
  return <Bot className="h-5 w-5 text-muted-foreground" />;
}

export function MentionMenu({
  open,
  candidates,
  highlightedIndex,
  onSelect,
  onHighlight,
}: MentionMenuProps) {
  const listRef = useRef<HTMLDivElement>(null);

  // Scroll the highlighted item into view on keyboard navigation.
  useEffect(() => {
    if (!open || candidates.length === 0) return;
    const node = listRef.current?.querySelector<HTMLElement>(
      `[data-mention-idx="${highlightedIndex}"]`,
    );
    node?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex, candidates.length, open]);

  if (!open) return null;

  return (
    <div className="absolute bottom-full left-0 right-0 px-0 pb-2 z-50">
      <div className="mx-0 border border-border/40 rounded-lg bg-popover shadow-xl overflow-hidden">
        <div className="px-3 py-1.5 border-b border-border/40 flex items-center gap-2">
          <span className="text-[10px] font-mono uppercase text-muted-foreground tracking-wider">
            Mention an agent
          </span>
          <span className="text-[10px] text-muted-foreground/70 ml-auto hidden sm:inline">
            ↑↓ navigate · ↵ select · esc cancel
          </span>
        </div>
        <div ref={listRef} className="p-1 max-h-[260px] overflow-y-auto">
          {candidates.length === 0 ? (
            <div className="px-3 py-3 text-sm text-muted-foreground italic">
              No agents match. Make sure the agent is attached to this chat.
            </div>
          ) : (
            candidates.map((c, i) => (
              <button
                type="button"
                key={c.dbSlug || c.mentionSlug}
                data-mention-idx={i}
                className={cn(
                  'w-full flex items-start gap-2.5 px-3 py-2 text-sm rounded-md text-left transition-colors',
                  i === highlightedIndex ? 'bg-accent' : 'hover:bg-accent/60',
                )}
                onClick={() => onSelect(c)}
                onMouseEnter={() => onHighlight(i)}
              >
                <div className="w-5 h-5 shrink-0 mt-0.5">
                  <AgentAvatar kind={c.kind} mentionSlug={c.mentionSlug} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{c.displayName}</span>
                    <span className="text-[10px] font-mono text-muted-foreground truncate">
                      @{c.mentionSlug}
                    </span>
                  </div>
                  {c.description && (
                    <div className="text-xs text-muted-foreground truncate">
                      {c.description}
                    </div>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
